import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, filter, finalize, from, mergeMap, of, switchMap, take, throwError, timeout } from 'rxjs';
import { EncryptionService } from '../encryption/encryption.service';
import { environment } from '../../../../environments/environment';
import { IRefreshTokenResponse, LoginService } from '../../login/login.service';
import { CVA_CONSTANTS } from '../../../util/constants';
import { MatDialog } from '@angular/material/dialog';
import { API_ROUTES } from '../../../util/route.constants';

@Injectable({
  providedIn: 'root'
})

export class ResponseInterceptorService implements HttpInterceptor {
  isRefreshing = false;
  refreshFailed = false;
  refreshTokenSubject = new BehaviorSubject<IRefreshTokenResponse | null>(null);

  constructor(
    private readonly router: Router,
    private readonly encryptionService: EncryptionService,
    private readonly loginService: LoginService,
    private dialog: MatDialog,
  ) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Logic to parse responses (Eg: redirect to login if error code is 401)
    return next.handle(request).pipe(mergeMap(event => this.decryptResponse(event)),
      catchError((error) => {
        const errorCode = error?.error?.code || error?.error?.error?.errorMessage;
        const isTokenExpired = error.status === 401 && errorCode === 'TOKEN_EXPIRED';
        const isRefreshUrl = request.url.includes('/custom/refresh');
        const isLogoutUrl = request.url.includes('/custom/logout');

        if (isRefreshUrl || isLogoutUrl) {
          if (isLogoutUrl) {
            console.error('[AUTH] logout api failed. token likely expired.', error);
          }
          this.handleLogout('', isRefreshUrl);
          if (isRefreshUrl) {
            const message = CVA_CONSTANTS.MESSAGE.SESSION_EXPIRED;
            return throwError(() => this.buildErrorWithMessage(error, message));
          }
          return throwError(() => error);
        }

        if (isTokenExpired) {
          return this.handleRefreshToken(request, next);
        }

        const statusCodes = [401, 423];
        const isRelevantStatus = statusCodes.includes(error.status) || statusCodes.includes(errorCode);

        if (isRelevantStatus) {
          const isLoggedIn = this.loginService.loggedIn$.value;

          const isPreAuthEndpoint = typeof error?.url === 'string' && (
            error.url.endsWith('custom/login') || error.url.includes(API_ROUTES.VALIDATE_LOGIN_OTP) ||
            error.url.endsWith(API_ROUTES.SESSION_LOGIN)
          );

          if (isLoggedIn) {
            const message = this.resolveLockedMessage(error.status, errorCode);
            this.handleLogout();
            return throwError(() => this.buildErrorWithMessage(error, message));
          }

          if (!isPreAuthEndpoint) {
            localStorage.clear();
            sessionStorage.clear();
          }
        }
        return throwError(() => error);
      })
    );
  }

  private resolveLockedMessage(status: number, errorCode: string): string {
    if (status !== 423) return '';
    const messages: Record<string, string> = {
      ERR_ACCOUNT_LOCKED: CVA_CONSTANTS.MESSAGE.ACCOUNT_LOCKED,
      ERR_ACCOUNT_INACTIVE: CVA_CONSTANTS.MESSAGE.INACTIVE_ACCOUNT,
    };
    return messages[errorCode] ?? '';
  }

  private decryptResponse(event: HttpEvent<unknown>): Observable<HttpEvent<unknown>> {
    if (!(event instanceof HttpResponse) || !event.url?.includes(environment.cva_url)) {
      return of(event);
    }
    return from(
      this.encryptionService.decryptAllTextFields(event.body).then(
        (decryptedBody) => event.clone({ body: decryptedBody })
      )
    );
  }

  private handleLogout(reason: string = '', redirectToLogin = true) {
    this.isRefreshing = false;
    this.refreshTokenSubject.next(null);
    this.loginService.revokeOnLogout(redirectToLogin, reason);
    this.dialog.closeAll();
  }

  private buildErrorWithMessage(error: HttpErrorResponse, message: string): HttpErrorResponse {
    return {
      ...error,
      message,
      errorMessage: message,
      error: {
        ...(error?.error ?? {}),
        message,
        errorMessage: message,
        status: error.status,
      },
    } as HttpErrorResponse;
  }

  private handleRefreshToken(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ) {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(tokens => tokens != null || this.refreshFailed),
        take(1),
        timeout(20000),
        switchMap((tokens) => {
          if (this.refreshFailed || !tokens || !this.loginService.loggedIn$.value) {
            return throwError(() => new Error('Session ended'));
          }
          const cloned =
            request.clone({
              setHeaders: {
                Authorization: `Bearer ${tokens!.idToken}`,
                'x-authorization-token': tokens!.authToken
              }
            });
          return next.handle(cloned).pipe(mergeMap(event => this.decryptResponse(event)));
        }), catchError(err => {
          console.error('[AUTH] queue branch fail. ', 'err=', err);
          return throwError(() => err);
        })
      );
    }

    this.isRefreshing = true;
    this.refreshFailed = false;
    this.refreshTokenSubject.next(null);
    return this.loginService.refreshToken().pipe(
      timeout(20000),
      switchMap((response: IRefreshTokenResponse) => {
        this.isRefreshing = false;
        const { authToken, idToken } = response;
        this.refreshTokenSubject.next(response);

        // retry original request
        const cloned = request.clone({
          setHeaders: {
            Authorization: `Bearer ${idToken}`,
            'x-authorization-token': authToken ?? ''
          }
        });

        return next.handle(cloned).pipe(mergeMap(event => this.decryptResponse(event)));
      }),

      catchError((err) => {
        this.isRefreshing = false;
        // if err already came from /custom/refresh call itself, intercept()'s own
        // catchError branch (isRefreshUrl) already ran handleLogout. avoid dupe here.
        const alreadyLoggedOut = err?.url?.includes('/custom/refresh');

        this.refreshFailed = true;
        this.refreshTokenSubject.next(null); // unblock queued reqs now flag true
        console.log(":", alreadyLoggedOut)
        if (!alreadyLoggedOut) {
          this.handleLogout(err?.errorMessage || '');
        }
        return throwError(() => err);
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }
}
